/**
 * App Insights limits:
 *   - Each property value (stringified): 8192 characters
 *   - Fallback sentinel used when depth is exceeded
 *
 * @see https://learn.microsoft.com/en-us/azure/azure-monitor/app/data-model-complete
 */
export const APP_INSIGHTS_MAX_PROPERTY_LENGTH = 8192;
export const TRUNCATED_SENTINEL = '[truncated]';

function isPlainObject(value: object): boolean {
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
}

function normaliseSingletonObject(value: object): unknown {
    if (value instanceof Date) {
        return isNaN(value.getTime()) ? 'Invalid Date' : value.toISOString();
    }
    if (value instanceof RegExp) {
        return value.toString();
    }
    if (value instanceof Error) {
        return { name: value.name, message: value.message, ...(value.stack && { stack: value.stack }) };
    }
    if (value instanceof Map) {
        const r: Record<string, unknown> = {};
        for (const [k, v] of value) {
            r[String(k)] = v;
        }
        return r;
    }
    if (value instanceof Set) {
        return Array.from(value);
    }
    return String(value);
}

/**
 * Recursively clips an object/array to the given depth.
 * Anything deeper than `depth` levels is replaced with `TRUNCATED_SENTINEL`.
 *
 * @param value
 * @param depth
 */
function clipDepth(value: unknown, depth: number): unknown {
    if (depth < 0) {
        return TRUNCATED_SENTINEL;
    }
    if (value === null || value === undefined) {
        return value;
    }
    if (typeof value !== 'object') {
        return value;
    }

    if (Array.isArray(value)) {
        if (depth === 0) {
            return TRUNCATED_SENTINEL;
        }
        return value.map((item) => clipDepth(item, depth - 1));
    }

    if (!isPlainObject(value)) {
        return clipDepth(normaliseSingletonObject(value), depth);
    }

    // Plain object
    if (depth === 0) {
        return TRUNCATED_SENTINEL;
    }

    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        result[k] = clipDepth(v, depth - 1);
    }
    return result;
}

/**
 * Truncates a single property value by reducing object/array nesting depth
 * until its JSON representation fits within `maxLength` characters.
 *
 * Primitive values (string, number, boolean, null, undefined) are returned
 * as-is (or coerced to string only when they already exceed the limit on their
 * own, which is an extreme edge case).
 *
 * @param value     - The value to truncate.
 * @param maxLength - Maximum allowed character length when serialised to JSON.
 * @param maxDepth  - Upper bound for depth search (guards against degenerate inputs).
 * @returns A value that serialises within the character budget, or the original
 *          primitive if it is already within budget.
 */
export function truncatePropertyValue(
    value: unknown,
    maxLength: number = APP_INSIGHTS_MAX_PROPERTY_LENGTH,
    maxDepth = 20,
): unknown {
    // Primitives that are not objects need no structural truncation.
    if (value === null || value === undefined) {
        return value;
    }

    const type = typeof value;
    if (type !== 'object' && type !== 'function') {
        // Primitive: just check if its string form is within budget.
        const asString = String(value);
        // return asString.length <= maxLength ? value : asString.slice(0, maxLength);
        if (asString.length <= maxLength) {
            return value;
        }

        // Try to parse as JSON — if it's an encoded object/array, truncate structurally
        // and re-encode rather than cutting the string mid-character.
        try {
            const parsed = JSON.parse(asString);
            if (parsed !== null && typeof parsed === 'object') {
                const truncated = truncatePropertyValue(parsed, maxLength, maxDepth);
                const reencoded = JSON.stringify(truncated);
                if (reencoded.length <= maxLength) {
                    return reencoded;
                }
            }
        } catch {
            // Not valid JSON — fall through to hard slice
        }

        return asString.slice(0, maxLength);
    }

    // For objects/arrays, try progressively shallower depths until it fits.
    for (let depth = maxDepth; depth >= 0; depth--) {
        const candidate = clipDepth(value, depth);
        try {
            if (JSON.stringify(candidate).length <= maxLength) {
                return candidate;
            }
        } catch {
            // JSON.stringify can throw on circular references etc. – keep trying.
        }
    }

    // Nothing fits — return the sentinel string.
    return TRUNCATED_SENTINEL;
}

/**
 * Sanitises a full App Insights `properties` map so that every value fits
 * within the App Insights character budget.
 *
 * - Handles `null` / `undefined` maps gracefully (returns `undefined`).
 * - Skips entries whose values are `null` or `undefined` (leaves them intact).
 * - Non-object top-level values (strings, numbers, …) are also checked and
 *   truncated if necessary.
 *
 * @param properties - The raw ICustomProperties map.
 * @param maxLength  - Per-value character budget (defaults to App Insights limit).
 * @returns A new map with all values within budget, or `undefined` if input was
 *          nullish.
 */
export function truncateAppInsightsProperties(
    properties: Record<string, unknown> | null | undefined,
    maxLength: number = APP_INSIGHTS_MAX_PROPERTY_LENGTH,
): Record<string, unknown> | undefined {
    if (properties === null) {
        return undefined;
    }
    if (typeof properties !== 'object' || Array.isArray(properties)) {
        return undefined;
    }

    const sanitised: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(properties)) {
        sanitised[key] = truncatePropertyValue(value, maxLength);
    }

    return sanitised;
}
